// Front-end mirror of the firmware variable resolver
// (firmware/src/scenes/canvas/canvas_variables.cpp).
//
// The device replaces {{type:format}} tokens inside text primitives at render
// time. To give the editor a true WYSIWYG preview we resolve the same grammar
// here, using live values where we can (time/date) and representative sample
// values otherwise (wifi, uptime, ...). The raw token always stays in the
// underlying `text` field — only the on-canvas rendering is substituted.

export type CanvasVariableContext = {
    displayDurationMs: number,
    elapsedMs: number,
    brightness: number,
    version: string,
};

export type CanvasVariableDef = {
    token: string,        // inserted into the text field
    labelKey: string,     // i18n key for the picker
    sample: string,       // shown next to the label in the picker
};

// The catalogue offered in the text "insert variable" picker.
export const CANVAS_VARIABLES: Array<CanvasVariableDef> = [
    { token: '{{time:HH:mm}}',        labelKey: 'page.scenes.canvas.variables.time',       sample: 'HH:mm' },
    { token: '{{time:HH:mm:ss}}',     labelKey: 'page.scenes.canvas.variables.time_sec',   sample: 'HH:mm:ss' },
    { token: '{{date:dd/MM/yyyy}}',   labelKey: 'page.scenes.canvas.variables.date',       sample: 'dd/MM/yyyy' },
    { token: '{{date:EEEE}}',         labelKey: 'page.scenes.canvas.variables.weekday',    sample: 'EEEE' },
    { token: '{{uptime:HH:mm:ss}}',   labelKey: 'page.scenes.canvas.variables.uptime',     sample: 'HH:mm:ss' },
    { token: '{{version}}',           labelKey: 'page.scenes.canvas.variables.version',     sample: '' },
    { token: '{{wifi:ssid}}',         labelKey: 'page.scenes.canvas.variables.wifi_ssid',   sample: '' },
    { token: '{{wifi:ip}}',           labelKey: 'page.scenes.canvas.variables.wifi_ip',     sample: '' },
    { token: '{{wifi:rssi}}',         labelKey: 'page.scenes.canvas.variables.wifi_rssi',   sample: '' },
    { token: '{{brightness}}',        labelKey: 'page.scenes.canvas.variables.brightness',  sample: '' },
    { token: '{{scene:s}}',           labelKey: 'page.scenes.canvas.variables.scene_s',     sample: '' },
    { token: '{{scene:percent}}',     labelKey: 'page.scenes.canvas.variables.scene_pct',   sample: '' },
];

// Representative sample values for things we cannot read in the browser.
const SAMPLE = {
    ssid: 'Lumeba-WiFi',
    ip: '192.168.1.42',
    rssi: -57,
    uptimeSeconds: 93784, // 1d 02:03:04
};

const pad = (n: number): string => (n < 10 ? '0' : '') + n;

// Map the firmware date-format tokens onto a value, using the longest tokens
// first (exactly like toStrftime in the firmware).
function formatDate(fmt: string, date: Date): string {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const daysShort = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const monthsShort = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    const h24 = date.getHours();
    const h12 = h24 % 12 === 0 ? 12 : h24 % 12;

    const tokens: Array<[string, string]> = [
        ['yyyy', String(date.getFullYear())],
        ['yy', String(date.getFullYear()).slice(-2)],
        ['MMMM', months[date.getMonth()]],
        ['MMM', monthsShort[date.getMonth()]],
        ['MM', pad(date.getMonth() + 1)],
        ['EEEE', days[date.getDay()]],
        ['EEE', daysShort[date.getDay()]],
        ['dd', pad(date.getDate())],
        ['HH', pad(h24)],
        ['hh', pad(h12)],
        ['mm', pad(date.getMinutes())],
        ['ss', pad(date.getSeconds())],
        ['a', h24 < 12 ? 'AM' : 'PM'],
    ];

    let result = '';
    let i = 0;
    while (i < fmt.length) {
        const match = tokens.find(([t]) => fmt.startsWith(t, i));
        if (match) {
            result += match[1];
            i += match[0].length;
        } else {
            result += fmt[i];
            i++;
        }
    }
    return result;
}

function formatUptime(fmt: string, totalSeconds: number): string {
    const d = Math.floor(totalSeconds / 86400);
    const h = Math.floor((totalSeconds % 86400) / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;

    const tokens: Array<[string, string]> = [
        ['dd', pad(d)],
        ['HH', pad(h)],
        ['mm', pad(m)],
        ['ss', pad(s)],
    ];

    let result = '';
    let i = 0;
    while (i < fmt.length) {
        const match = tokens.find(([t]) => fmt.startsWith(t, i));
        if (match) {
            result += match[1];
            i += match[0].length;
        } else {
            result += fmt[i];
            i++;
        }
    }
    return result;
}

function resolveOne(type: string, format: string, ctx: CanvasVariableContext): string {
    const now = new Date();
    switch (type) {
        case 'time':
        case 'date':
            return formatDate(format, now);
        case 'uptime':
            return formatUptime(format || 'HH:mm:ss', SAMPLE.uptimeSeconds);
        case 'wifi':
            if (format === 'ssid') return SAMPLE.ssid;
            if (format === 'ip') return SAMPLE.ip;
            if (format === 'rssi') return `${SAMPLE.rssi}dBm`;
            return '';
        case 'version':
            return ctx.version;
        case 'brightness':
            return String(ctx.brightness);
        case 'scene': {
            const elapsed = Math.min(ctx.elapsedMs, ctx.displayDurationMs);
            const remaining = ctx.displayDurationMs - elapsed;
            if (format === 'ms') return String(remaining);
            if (format === 's') return String(Math.floor(remaining / 1000));
            if (format === 'percent') {
                if (ctx.displayDurationMs === 0) return '0';
                return String(Math.round(100 - (100 * elapsed) / ctx.displayDurationMs));
            }
            return '';
        }
        default:
            return '';
    }
}

export function resolveVariables(text: string, ctx: CanvasVariableContext): string {
    if (text.indexOf('{{') < 0) return text;

    let result = '';
    let i = 0;
    while (i < text.length) {
        if (text.startsWith('{{', i)) {
            const end = text.indexOf('}}', i + 2);
            if (end < 0) {
                result += text[i++];
                continue;
            }
            const content = text.substring(i + 2, end);
            const colon = content.indexOf(':');
            const type = colon >= 0 ? content.substring(0, colon) : content;
            const format = colon >= 0 ? content.substring(colon + 1) : '';
            result += resolveOne(type, format, ctx);
            i = end + 2;
        } else {
            result += text[i++];
        }
    }
    return result;
}
